import type { ReactNode } from "react";

import { useT } from "../i18n";

/**
 * A casca das telas que também são seção do jogo.
 *
 * Existiam quatro cascas diferentes para a mesma coisa: uma na loja, uma nas
 * ligas, uma no mercado online e outra na conta, cada uma com a própria
 * largura máxima, o próprio recuo, o próprio fundo e o próprio jeito de
 * alinhar o título. Nenhuma delas parecia com elenco e tática, que são as
 * telas que a pessoa vê o tempo todo.
 *
 * **Dentro do jogo, a casca some.** O hub já dá coluna, recuo e fundo, e uma
 * segunda casca por cima disso é o que fazia loja e conta aparecerem
 * centralizadas e estreitas ao lado de um elenco que ocupa a coluna inteira.
 * Quem carrega o rótulo ali é o cabeçalho de cada painel, exatamente como em
 * "Elenco principal" e "Escalação": sem título de página, porque a barra de
 * navegação já diz onde a pessoa está.
 *
 * **Solta, a casca volta.** Conta e opções ainda são alcançadas da abertura,
 * por quem entrou e ainda não tem carreira. Ali não existe hub, então elas
 * precisam do centro, da largura máxima e do próprio botão de voltar.
 *
 * Quem decide qual das duas é `aoVoltar`, e não uma bandeira à parte: o botão
 * de voltar só faz sentido onde não há barra de navegação, então a presença
 * dele já é a pergunta respondida.
 */
export function Pagina({
  titulo,
  subtitulo,
  aoVoltar,
  children,
}: {
  titulo: string;
  /** Só quando diz um dado da tela, como o estado da liga. Nunca para explicar a tela. */
  subtitulo?: string | undefined;
  aoVoltar?: (() => void) | undefined;
  children: ReactNode;
}): ReactNode {
  const t = useT();

  if (!aoVoltar) {
    return (
      <div
        className="grid"
        style={{
          gap: 14,
          /* Sem isto, a grade herda a altura do flex esticado e reparte a sobra
             entre as linhas: o último painel vira um bloco alto. */
          alignContent: "start",
        }}
      >
        {subtitulo && (
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
            {subtitulo}
          </p>
        )}
        {children}
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        padding: "32px var(--gutter)",
        background: "var(--bg)",
      }}
    >
      <div style={{ width: "min(820px, 100%)", display: "grid", gap: 14, alignContent: "start" }}>
        <header style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 24, letterSpacing: "-0.01em" }}>{titulo}</h1>
          {subtitulo && (
            <p className="muted" style={{ margin: "6px auto 0", maxWidth: 460, fontSize: 12.5 }}>
              {subtitulo}
            </p>
          )}
        </header>
        {children}
        <button className="ghost" onClick={aoVoltar} style={{ justifySelf: "center" }}>
          {t("comum.voltar")}
        </button>
      </div>
    </div>
  );
}
